import type { User } from "@src/domain/users/entities/user.entity";
import type { IUserRepository, PaginationParams, SearchParams, PaginatedResult } from "@src/domain/users/repository/user.repository.interface";
import type { CreateUserInputDTO, UpdateUserInputDTO, UpdateProfileInputDTO } from "@src/application/users/dto/user-input.dto";
import type { ApiClient } from "../api/client";
import { UserMapper } from "../mappers/user_mapper";

/**
 * User Repository Implementation
 * Handles HTTP communication for user management operations
 */
export class UserRepositoryImpl implements IUserRepository {
    private readonly apiClient: ApiClient;

    constructor(apiClient: ApiClient) {
        this.apiClient = apiClient;
    }

    /**
     * Create a new user with specific roles (Admin only)
     */
    async create(data: CreateUserInputDTO): Promise<User> {
        const response = await this.apiClient.post<any>("/admin/users", data);
        return UserMapper.toDomain(response);
    }

    /**
     * List all users with pagination (Admin only)
     */
    async listAll(params?: PaginationParams): Promise<PaginatedResult<User>> {
        const queryParams = new URLSearchParams();

        if (params?.page) {
            queryParams.append('page', params.page.toString());
        }
        if (params?.page_size) {
            queryParams.append('page_size', params.page_size.toString());
        }

        const queryString = queryParams.toString();
        const url = queryString ? `/admin/users?${queryString}` : '/admin/users';

        const response = await this.apiClient.get<any>(url);

        // Debug: Log the API response structure
        console.log('[List Users API Response]', response);

        // Handle both array response and paginated response
        if (Array.isArray(response)) {
            // If response is just an array, create a simple paginated result
            const users = response.map((u: any) => UserMapper.toDomain(u));
            return {
                items: users,
                total: users.length,
                page: 1,
                page_size: users.length,
                total_pages: 1,
                hasMore: false, // No more pages for array response
            };
        } else {
            // If response is paginated
            const users = response.users ? response.users.map((u: any) => UserMapper.toDomain(u)) :
                response.items ? response.items.map((u: any) => UserMapper.toDomain(u)) : [];

            // Calculate hasMore
            let hasMore = false;
            if (response.has_more !== undefined) {
                hasMore = response.has_more;
            } else if (response.hasMore !== undefined) {
                hasMore = response.hasMore;
            } else if (response.page && response.total_pages) {
                hasMore = response.page < response.total_pages;
            } else {
                // Fallback: check if we got fewer results than requested
                hasMore = !(users.length < (params?.page_size || 50));
            }

            return {
                items: users,
                total: response.total || users.length,
                page: response.page || 1,
                page_size: response.page_size || users.length,
                total_pages: response.total_pages || 1,
                hasMore,
            };
        }
    }

    /**
     * Search users with filters and pagination (Admin only)
     */
    async search(params?: SearchParams): Promise<PaginatedResult<User>> {
        const queryParams = new URLSearchParams();

        if (params?.email) {
            queryParams.append('email', params.email);
        }
        if (params?.name) {
            queryParams.append('name', params.name);
        }
        if (params?.phone) {
            queryParams.append('phone', params.phone);
        }
        if (params?.skip !== undefined) {
            queryParams.append('skip', params.skip.toString());
        }
        if (params?.limit !== undefined) {
            queryParams.append('limit', params.limit.toString());
        }

        const queryString = queryParams.toString();
        const url = queryString ? `/admin/users/search?${queryString}` : '/admin/users/search';

        const response = await this.apiClient.get<any>(url);

        // Debug: Log the API response structure
        console.log('[Search Users API Response]', response);

        // Assume response is paginated
        const users = response.users ? response.users.map((u: any) => UserMapper.toDomain(u)) :
            response.items ? response.items.map((u: any) => UserMapper.toDomain(u)) :
                response.data ? response.data.map((u: any) => UserMapper.toDomain(u)) : [];

        // Calculate hasMore
        let hasMore = false;
        if (response.has_more !== undefined) {
            hasMore = response.has_more;
        } else if (response.hasMore !== undefined) {
            hasMore = response.hasMore;
        } else if (response.total && params?.limit) {
            hasMore = (params.skip || 0) + users.length < response.total;
        } else {
            // Fallback: check if we got the limit number of results
            hasMore = !(users.length < (params?.limit || 50));
        }

        return {
            items: users,
            total: response.total || users.length,
            page: Math.floor((params?.skip || 0) / (params?.limit || 50)) + 1,
            page_size: params?.limit || users.length,
            total_pages: response.total_pages || Math.ceil((response.total || users.length) / (params?.limit || 50)),
            hasMore,
        };
    }

    /**
     * Get a specific user by ID (Admin only)
     */
    async getById(userId: string): Promise<User | null> {
        try {
            const data = await this.apiClient.get<any>(`/admin/users/${userId}`);
            return data ? UserMapper.toDomain(data) : null;
        } catch (error: any) {
            if (error.response?.status === 404) {
                return null;
            }
            throw error;
        }
    }

    /**
     * Update user information (Admin only)
     */
    async update(userId: string, data: UpdateUserInputDTO): Promise<User> {
        const response = await this.apiClient.put<any>(`/admin/users/${userId}`, data);
        return UserMapper.toDomain(response);
    }

    /**
     * Ban a user from accessing the system (Admin only)
     */
    async ban(userId: string): Promise<User> {
        const response = await this.apiClient.post<any>(`/admin/users/${userId}/ban`, {});
        return UserMapper.toDomain(response);
    }

    /**
     * Unban a user (Admin only)
     */
    async unban(userId: string): Promise<User> {
        const response = await this.apiClient.post<any>(`/admin/users/${userId}/unban`, {});
        return UserMapper.toDomain(response);
    }

    /**
     * Update authenticated user's profile
     */
    async updateProfile(data: UpdateProfileInputDTO): Promise<User> {
        const response = await this.apiClient.put<any>("/auth/profile", data);
        return UserMapper.toDomain(response);
    }

    /**
     * Upload profile picture for authenticated user
     */
    async uploadProfilePicture(file: File, onUploadProgress?: (progress: number) => void): Promise<User> {
        const response = await this.apiClient.uploadFile<any>("/auth/profile/picture", {
            file: file,
            onUploadProgress: onUploadProgress ? (progressEvent: any) => {
                if (progressEvent.total) {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    onUploadProgress(percentCompleted);
                }
            } : undefined,
        });

        return UserMapper.toDomain(response);
    }
}
